/**
 * InternalLoadHistograms – histogram view for the Internal Loads tab.
 * Full parity with ExternalLoadHistograms: date navigation, period (AM/PM),
 * drill chips, latest mode, metric selector, drag-to-reorder.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Loader2,
  Brain, Activity, Flame, AlertTriangle, BarChart3, Gauge,
  ArrowUpAZ, ArrowUp01, Globe2, CircleDot, CalendarDays, Sun, Timer, Trophy,
} from "lucide-react";
import MetricVisibilitySidebar from "./MetricVisibilitySidebar";
import HistogramChart from "./HistogramChart.jsx";
import { useInternalLoadDrills, GLOBAL_DRILL } from "./useInternalLoadDrills.js";
import { normalizePlayerPosition } from "../common/PlayerPositions.jsx";

/* ─── helpers ─── */
const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isPositiveNumber = (v) => {
  const n = toNumberOrNull(v);
  return n !== null && n > 0;
};

const sortAlpha = (a, b) => {
  const cmp = (a.last_name ?? "").localeCompare(b.last_name ?? "");
  return cmp !== 0 ? cmp : (a.first_name ?? "").localeCompare(b.first_name ?? "");
};

const toTrainabilityScore10 = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const v = Number(raw);
  if (!Number.isFinite(v)) return null;
  if (v >= 0 && v <= 1) return v * 10;
  return v;
};

/* ─── drill category classifier (same as External) ─── */
const DRILL_CATEGORIES_DEF = [
  { key: "__global__", regex: null,                              color: "#6366f1", Icon: Globe2      },
  { key: "training",   regex: /entra[iî]n|training/i,           color: "#3b82f6", Icon: Brain       },
  { key: "warmup",     regex: /[eé]chauff|warm/i,               color: "#f59e0b", Icon: Sun         },
  { key: "match",      regex: /\bmatch\b|\bjour\s*de\s*match/i, color: "#ef4444", Icon: Trophy      },
  { key: "mt_md",      regex: /\b(mt|md)[\s\-]?\d/i,           color: "#8b5cf6", Icon: CalendarDays },
  { key: "halftime",   regex: /mi[\s\-]?temps|half[\s\-]?time/i,color: "#10b981", Icon: Timer      },
  { key: "session",    regex: /session/i,                        color: "#64748b", Icon: CircleDot  },
];

function classifyDrill(name) {
  if (!name) return "other";
  for (const cat of DRILL_CATEGORIES_DEF) {
    if (cat.regex && cat.regex.test(name)) return cat.key;
  }
  return "other";
}

/* ─── metric keys ─── */
const DEFAULT_ORDER = [
  "trainability_index",
  "mechanical_fatigue",
  "intensity_index",
  "RII",
  "mechanical_load_index",
  "MLI",
];
const RESTRICTED_ORDER = ["trainability_index", "mechanical_fatigue"];

/* ─── drillKey map: metric key → drill data key ─── */
const DRILL_KEYS = {
  trainability_index:    "trainability",
  mechanical_fatigue:    "mechanical_fatigue",
  intensity:       "intensity",        // ← change ici ! utilise "intensity"
  mechanical_load: "mechanical_load",  // ← change ici ! utilise "mechanical_load"
  RII:                   "RII",
  MLI:                   "MLI",
};

const POSITIONS = [
  { value: "All",      labelKey: "positions.all"      },
  { value: "Defense",  labelKey: "positions.defense"  },
  { value: "Midfield", labelKey: "positions.midfield" },
  { value: "Forward",  labelKey: "positions.forward"  },
  { value: "Lateral",  labelKey: "positions.lateral"  },
];

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function InternalLoadHistograms({
  players = [],
  isDarkMode = false,
  selectedPosition = "All",
  onPositionChange,
  onPlayerClick,
  season,
  visibleMetrics: visibleMetricsProp,
  onVisibleMetricsChange,
  restrictedMetrics = false,
}) {
  const { t } = useTranslation();
  const allowedPlayerIds = useMemo(
    () => new Set(players.map((player) => String(player.id))),
    [players]
  );
  const matchesSelectedPosition = useCallback(
    (position) =>
      selectedPosition === "All" ||
      normalizePlayerPosition(position) === selectedPosition,
    [selectedPosition]
  );
  const playerMap = useMemo(() => {
    const map = {};
    players.forEach(p => {
      map[p.id] = p;
    });
    return map;
  }, [players]);
  /* ─── metric definitions (translated) ─── */
  const METRICS = useMemo(() => [
    {
      key: "trainability_index",
      drillKey: DRILL_KEYS.trainability_index,
      label: t("metrics.trainability"),
      unit: "/ 10",
      color: "#16a34a",
      icon: Brain,
      format: (v) => (v != null ? Number(v).toFixed(1) : "–"),
      fromPlayer: (p) => toTrainabilityScore10(p.trainability_index),
    },
    {
      key: "mechanical_fatigue",
      drillKey: DRILL_KEYS.mechanical_fatigue,
      label: t("metrics.mechanical_fatigue"),
      unit: "%",
      color: "#f59e0b",
      icon: AlertTriangle,
      format: (v) => (v != null ? `${Number(v).toFixed(1)}` : "–"),
      fromPlayer: (p) => {
        const n = toNumberOrNull(p.mechanical_fatigue);
        return n !== null ? n * 100 : null;
      },
    },
    {
      key: "intensity_index",
      drillKey: DRILL_KEYS.intensity,
      label: t("metrics.intensity"),
      unit: "PT",
      color: "#6366f1",
      icon: Activity,
      format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
      fromPlayer: (p) => toNumberOrNull(p.intensity_index),
    },
    {
      key: "RII",
      drillKey: DRILL_KEYS.RII,
      label: t("metrics.rii"),
      unit: "%",
      color: "#8b5cf6",
      icon: Flame,
      format: (v) => (v != null ? `${Number(v).toFixed(1)}` : "–"),
      fromPlayer: (p) => {
        const n = toNumberOrNull(p.RII);
        return n !== null ? n * 100 : null;
      },
    },
    {
      key: "mechanical_load_index",
      drillKey: DRILL_KEYS.mechanical_load,
      label: t("metrics.mechanical_load"),
      unit: "PT",
      color: "#64748b",
      icon: Gauge,
      format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
      fromPlayer: (p) => toNumberOrNull(p.mechanical_load_index),
    },
    {
      key: "MLI",
      drillKey: DRILL_KEYS.MLI,
      label: t("metrics.mli"),
      unit: "%",
      color: "#10b981",
      icon: BarChart3,
      format: (v) => (v != null ? `${Number(v).toFixed(1)}` : "–"),
      fromPlayer: (p) => {
        const n = toNumberOrNull(p.MLI);
        return n !== null ? n * 100 : null;
      },
    },
  ], [t]);

  const METRICS_BY_KEY = useMemo(
    () => Object.fromEntries(METRICS.map((m) => [m.key, m])),
    [METRICS]
  );

  /* ─── state ─── */
  const [highlightId, setHighlightId]         = useState(null);
  const [sortMode, setSortMode]               = useState("alpha");
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [isStuck, setIsStuck]                 = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return sessionStorage.getItem("internalLoad_sidebarOpen") === "true"; } catch {} return false;
  });

  const [metricOrder, setMetricOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("internalLoad_metricOrder");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          Array.isArray(parsed) &&
          parsed.length === DEFAULT_ORDER.length &&
          DEFAULT_ORDER.every((k) => parsed.includes(k)) &&
          parsed.every((k) => DEFAULT_ORDER.includes(k))
        ) return parsed;
      }
    } catch {}
    localStorage.removeItem("internalLoad_metricOrder");
    return DEFAULT_ORDER;
  });

  const [visibleMetricsLocal, setVisibleMetricsLocal] = useState(() => {
    try {
      const saved = sessionStorage.getItem("internalLoad_visibleMetrics");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch {}
    return new Set(DEFAULT_ORDER);
  });

  const visibleMetrics = visibleMetricsProp ?? visibleMetricsLocal;
  const renderedMetricKeys = restrictedMetrics
    ? RESTRICTED_ORDER
    : metricOrder.filter((key) => visibleMetrics.has(key));
  const setVisibleMetrics = (valOrFn) => {
    const next = typeof valOrFn === "function" ? valOrFn(visibleMetrics) : valOrFn;
    if (onVisibleMetricsChange) onVisibleMetricsChange(next);
    else setVisibleMetricsLocal(next);
    sessionStorage.setItem("internalLoad_visibleMetrics", JSON.stringify([...next]));
  };

  /* ─── refs ─── */
  const dragItem         = useRef(null);
  const dragOverItem     = useRef(null);
  const containerRef     = useRef(null);
  const sentinelRef      = useRef(null);

  /* ─── drag & drop reorder ─── */
  const handleDragStart = (index) => { dragItem.current = index; };
  const handleDragEnter = (index) => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    const newOrder = [...metricOrder];
    const dragged = newOrder.splice(dragItem.current, 1)[0];
    newOrder.splice(dragOverItem.current, 0, dragged);
    setMetricOrder(newOrder);
    localStorage.setItem("internalLoad_metricOrder", JSON.stringify(newOrder));
    dragItem.current = null;
    dragOverItem.current = null;
  };

  /* ─── sticky toolbar detection ─── */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-61px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isStuck) setToolbarCollapsed(false);
  }, [isStuck]);

  /* ─── close highlight on outside click ─── */
  useEffect(() => {
    if (highlightId == null) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setHighlightId(null);
    };
    document.addEventListener("pointerdown", handle);
    return () => document.removeEventListener("pointerdown", handle);
  }, [highlightId]);



  const handleBarClick = useCallback(
    (id) => setHighlightId((prev) => (prev === id ? null : id)),
    []
  );

  /* ─── date / drill / period hook ─────────────────────────────────────────
   * On réutilise useExternalLoadDrills en lui passant category="internal".
   * Si le hook ne supporte pas encore ce paramètre, voir la note en bas
   * du fichier pour l'adaptation nécessaire.
   * ─────────────────────────────────────────────────────────────────────── */
  const {
    dates,
    loadingDates,
    selectedDate,
    setSelectedDate,
    loadingDrills,
    drillNames,
    selectedDrill,
    setSelectedDrill,
    playerMetrics,
    availablePeriods,
    selectedPeriod,
    setSelectedPeriod,
  } = useInternalLoadDrills(season);// ← "internal" category

  const isLatestMode  = selectedDate == null;
  const hasDrillData  = !isLatestMode && playerMetrics && Object.keys(playerMetrics).length > 0;
  const isTransitioning = loadingDrills;

  const handleDateChange = useCallback((val) => setSelectedDate(val), [setSelectedDate]);

  /* ─── date navigator helpers ─── */
  const allDates = [...dates];
  const dateIdx  = allDates.indexOf(selectedDate);
  const canPrev  = dateIdx < allDates.length - 1;
  const canNext  = dateIdx > 0;

  const formatNavDate = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  };

  /* ─── drill icon map ─── */
  const drillIconMap = useMemo(() => {
    const map = {};
    for (const name of drillNames) {
      const catKey = classifyDrill(name);
      const cat    = DRILL_CATEGORIES_DEF.find((c) => c.key === catKey);
      map[name]    = cat?.Icon ?? CircleDot;
    }
    return map;
  }, [drillNames]);

  /* ─── chart data ─── */
  const dataByKey = useMemo(() => {
    const map = {};

    METRICS.forEach((m) => {
      // ── Latest mode or no drill data: use player props directly
      if (isLatestMode || !hasDrillData) {
        map[m.key] = players
          .filter((p) => matchesSelectedPosition(p.position))
          .map((p) => ({
            id: p.id,
            first_name: p.first_name,
            last_name: p.last_name,
            profile_picture: p.profile_picture,
            position: p.position,
            value: m.fromPlayer(p),
            trainability_index: p.trainability_index,
            trainability_level: p.trainability_level,
          }))
          
.filter((p) => {
  const n = toNumberOrNull(p.value);
  return n !== null && Number.isFinite(n);
})

          .sort(
            sortMode === "asc"
              ? (a, b) => (a.value ?? -Infinity) - (b.value ?? -Infinity)
              : sortAlpha
          );
        return;
      }
      // ── Drill mode: use playerMetrics from hook
      const metrics = playerMetrics || {};
    
      map[m.key] = Object.values(metrics)
        .filter(
          (p) =>
            allowedPlayerIds.has(String(p.player_id)) &&
            matchesSelectedPosition(p.position)
        )
        .map((p) => {
          
          const raw   = p.metrics?.[m.drillKey] ?? null;
          const n     = toNumberOrNull(raw);
          
          // apply same transforms as fromPlayer (fatigue ×100, trainability ×10, RII/MLI ×100)
          let value = n;
          if (n !== null) {
            if (m.key === "mechanical_fatigue" || m.key === "RII" || m.key === "MLI") {
              value = n * 100;
            } else if (m.key === "trainability_index") {
              value = toTrainabilityScore10(n);
            }
          }
            const originalPlayer = playerMap[p.player_id];
            
    return {
      id:                p.player_id,
      first_name:        p.first_name,
      last_name:         p.last_name,
      // Priorité: d'abord les données du drill, sinon celles du joueur original
      profile_picture:   p.profile_picture || originalPlayer?.profile_picture || null,
      position:          p.position,
      value,
      trainability_index: p.trainability_index ?? originalPlayer?.trainability_index ?? null,
      trainability_level: p.trainability_level ?? originalPlayer?.trainability_level ?? null,
    };
  })

.filter((p) => {
  const n = toNumberOrNull(p.value);
  return n !== null && Number.isFinite(n);
})
  .sort(
    sortMode === "asc"
      ? (a, b) => (a.value ?? -Infinity) - (b.value ?? -Infinity)
      : sortAlpha
  );
    });

    return map;
  }, [METRICS, players, playerMetrics, isLatestMode, hasDrillData, sortMode, allowedPlayerIds, matchesSelectedPosition]);

  /* ═══════════════════════════════════════════════════════════════════════ */
  /* RENDER                                                                  */
  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-5">
      {/* Sentinel for sticky detection */}
      <div ref={sentinelRef} className="h-0" />

      {/* ─── Sticky Toolbar ─── */}
      <div
        className={`sticky top-[60px] z-[15] -mx-6 px-6 transition-all duration-300 ${
          toolbarCollapsed ? "pt-1 pb-0" : "pt-3 pb-1"
        } ${isDarkMode ? "bg-gray-900/90 backdrop-blur-md" : "bg-gray-100/90 backdrop-blur-md"}`}
        style={{ position: "sticky" }}
      >
        {isStuck && (
          <div className={`absolute bottom-0 left-4 right-4 h-px ${
            isDarkMode
              ? "bg-gradient-to-r from-transparent via-gray-500/40 to-transparent"
              : "bg-gradient-to-r from-transparent via-gray-400/30 to-transparent"
          }`} />
        )}

        {/* Collapsible content */}
        <div className={`overflow-visible transition-all duration-300 ease-in-out ${
          toolbarCollapsed ? "max-h-0 opacity-0 overflow-hidden" : "opacity-100"
        }`}>

          {/* ── Row 1 : filters ── */}
          <div className="flex flex-wrap items-center gap-3">

            {/* Position filter */}
            <select
              className={`p-2.5 border rounded-lg text-sm shadow-sm focus:ring focus:ring-blue-300 ${
                isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-800"
              }`}
              value={selectedPosition}
              onChange={(e) => onPositionChange?.(e.target.value)}
            >
     {POSITIONS.map((pos) => (
  <option key={pos.value} value={pos.value}>
    {t(pos.labelKey)}
  </option>
))}
            </select>

            {/* Sort toggle */}
            <div className={`inline-flex items-center rounded-lg border p-0.5 ${
              isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"
            }`}>
              <button
                onClick={() => setSortMode("alpha")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  sortMode === "alpha"
                    ? isDarkMode ? "bg-gray-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                }`}
                title={t("external_load.sort_alpha")}
              >
                <ArrowUpAZ className="h-3.5 w-3.5" />
                A-Z
              </button>
              <button
                onClick={() => setSortMode("asc")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  sortMode === "asc"
                    ? isDarkMode ? "bg-gray-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                }`}
                title={t("external_load.sort_asc")}
              >
                <ArrowUp01 className="h-3.5 w-3.5" />
                0-9
              </button>
            </div>

            {/* Separator */}
            <div className={`h-6 w-px ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />

            {/* ── Date navigator ── */}
            <div className="flex items-center gap-1.5">
              <button
                disabled={!canPrev || loadingDates}
                onClick={() => canPrev && handleDateChange(allDates[dateIdx + 1])}
                className={`p-1.5 rounded-md transition ${
                  isDarkMode
                    ? "hover:bg-gray-700 disabled:opacity-30 text-gray-300"
                    : "hover:bg-gray-100 disabled:opacity-30 text-gray-600"
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <select
                className={`px-2 py-2.5 rounded-lg border text-sm ${
                  isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-800"
                }`}
                value={selectedDate ?? ""}
                onChange={(e) => handleDateChange(e.target.value)}
              >
                {dates.map((d) => (
                  <option key={d} value={d}>{formatNavDate(d)}</option>
                ))}
              </select>
              <button
                disabled={!canNext || loadingDates}
                onClick={() => canNext && handleDateChange(allDates[dateIdx - 1])}
                className={`p-1.5 rounded-md transition ${
                  isDarkMode
                    ? "hover:bg-gray-700 disabled:opacity-30 text-gray-300"
                    : "hover:bg-gray-100 disabled:opacity-30 text-gray-600"
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {loadingDates && (
              <div className="flex items-center gap-1.5">
                <Loader2 className={`h-4 w-4 animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("common.loading")}
                </span>
              </div>
            )}

          </div>{/* end row 1 */}

        </div>{/* end collapsible */}

        {/* Collapse / Expand toggle */}
        {isStuck && (
          <div className="flex justify-center pt-0.5 pb-1">
            <button
              onClick={() => setToolbarCollapsed((v) => !v)}
              className={`flex items-center justify-center w-10 h-4 rounded-full transition-all ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-100 bg-gray-700/50 hover:bg-gray-600/70"
                  : "text-gray-400 hover:text-gray-600 bg-gray-200/60 hover:bg-gray-300/70"
              }`}
              title={toolbarCollapsed ? t("external_load.show_filters") : t("external_load.hide_filters")}
            >
              {toolbarCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>

      {/* ─── Histogram charts ─── */}
      <div className="space-y-6" ref={containerRef}>
        {renderedMetricKeys
          .map((metricKey, index) => {
            const metric = METRICS_BY_KEY[metricKey];
            if (!metric) return null;
              
            return (
              <div
                key={metric.key}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className={`transition-opacity cursor-grab active:cursor-grabbing ${
                  dragItem.current === index ? "opacity-40" : "opacity-100"
                }`}
              >
           
                <HistogramChart
                  title={metric.label}
                  unit={metric.unit}
                  color={metric.color}
                  icon={metric.icon}
                  players={dataByKey[metric.key] ?? []}
                  highlightId={highlightId}
                  onBarClick={handleBarClick}
                  isDarkMode={isDarkMode}
                  formatValue={metric.format}
                  isLoading={isTransitioning}
                />
              </div>
            );
          })}
      </div>

      {/* ─── Sticky right sidebar for metric toggles ─── */}
      {!restrictedMetrics && (
        <MetricVisibilitySidebar
          metrics={DEFAULT_ORDER.map((k) => METRICS_BY_KEY[k]).filter(Boolean)}
          visibleMetrics={visibleMetrics}
          onChange={setVisibleMetrics}
          allKeys={DEFAULT_ORDER}
          storageKey="internalLoad_sidebarOpen"
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

